import { useT } from '../../lib/i18n';
import { ScreenHeading, Skeleton } from '../shell/PortalShell';
import type { Staff } from '../../lib/types';

/**
 * Specialist picker.
 *
 * "Any available" is offered first and pre-selected, because it genuinely gives
 * the customer more times to choose from — the server unions every eligible
 * employee's free slots. The list only contains staff who can perform all the
 * selected services; the server filters that, so nothing here can offer a
 * combination the salon cannot deliver.
 */
export function StaffStep({
  staff,
  loading,
  selectedId,
  allowAny,
  onSelect,
}: {
  staff: Staff[];
  loading: boolean;
  selectedId: number | null;
  allowAny: boolean;
  onSelect: (id: number | null) => void;
}): JSX.Element {
  const { t, pick } = useT();

  if (loading) {
    return (
      <div>
        <ScreenHeading title={t('choose_staff')} subtitle={t('choose_staff_sub')} />
        <Skeleton rows={3} />
      </div>
    );
  }

  if (staff.length === 0) {
    return (
      <div className="bk-rise">
        <ScreenHeading title={t('choose_staff')} />
        <p className="bk-card p-6 text-center text-sm text-zinc-500">{t('no_staff')}</p>
      </div>
    );
  }

  return (
    <div className="bk-rise">
      <ScreenHeading title={t('choose_staff')} subtitle={allowAny ? t('choose_staff_sub') : undefined} />
      <div className="grid gap-2.5 sm:grid-cols-2">
        {allowAny && (
          <button
            type="button"
            className="bk-tile flex items-center gap-3"
            data-selected={selectedId === null}
            onClick={() => onSelect(null)}
          >
            <span
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
              aria-hidden
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-zinc-900">{t('any_staff')}</span>
              <span className="block text-xs text-zinc-500">{t('any_staff_sub')}</span>
            </span>
          </button>
        )}

        {staff.map((member) => (
          <button
            key={member.id}
            type="button"
            className="bk-tile flex items-center gap-3"
            data-selected={selectedId === member.id}
            onClick={() => onSelect(member.id)}
          >
            <span
              className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-sm font-semibold text-white"
              style={{ background: member.color ?? 'var(--accent)' }}
              aria-hidden
            >
              {initials(pick(member))}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-900">{pick(member)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase();
}
